import React from 'react'
import {useSelector} from 'react-redux'
import {BrowserRouter, Navigate, Route, Routes} from 'react-router-dom'
import PrivateRoutes from './PrivateRoute'

import ForgotPassword from '../app/components/auth/ForgotPassword'
import ResetPassword from '../app/components/auth/ResetPassword'
import Signup from '../app/components/auth/Signup'
import Otp from '../app/components/auth/otp'
import CreateNewPassword from '../app/components/auth/createNewPassword'
import TOC from '../app/components/auth/Toc'
import InactivityModal from '../app/common/InactivityModal'
import SelectLoginUser from '../app/components/auth/SelectLogin'
import HandleGoogleRedirect from '../app/components/auth/HandleGoogleRedircet'
import HandleMSALRedirect from '../app/components/auth/HandleMSALRedirect'
import PrivacyPolicy from '../app/common/PrivacyPolicy'
import RefundPolicy from '../app/common/RefundPolicy'
import MobileLogin from '../app/components/auth/MobileLogin'
import AuthLayout from '../app/components/auth/AuthLayout'
import LoginForm2 from '../app/components/auth/LoginForm2'
import WeightHarness from '../app/components/weight/__harness__/WeightHarness'

// Scroll to top on route change
import {useEffect, useRef} from 'react'
import {useLocation} from 'react-router-dom'
import {startHeartbeat, stopHeartbeat, trackNav, getSessionId, normaliseRoute, getPageMeta, PRODUCT} from '../app/analytics'

function ScrollToTop() {
  const {pathname} = useLocation()
  const prev = useRef(pathname)
  useEffect(() => {
    // Skip the reset when moving between sub-tabs of the same single-page section
    // (e.g. /serviceable-pincodes/forward → /sdd) — those handle their own in-page scroll.
    const inServiceability = (p) => p.startsWith('/serviceable-pincodes')
    if (!(inServiceability(prev.current) && inServiceability(pathname))) {
      window.scrollTo(0, 0)
    }
    prev.current = pathname
  }, [pathname])
  return null
}

// Fires a NAVIGATION event on route change, recording time spent on the previous page
function NavigationTracker() {
  const {pathname} = useLocation()
  const prevRef = useRef(null)
  const enteredAtRef = useRef(Date.now())

  useEffect(() => {
    if (prevRef.current !== null && prevRef.current !== pathname) {
      const elapsed = Math.round((Date.now() - enteredAtRef.current) / 1000)
      const norm = normaliseRoute(prevRef.current)
      const {moduleName, pageName} = getPageMeta(norm)
      trackNav({sessionId: getSessionId(), currentRoute: norm, activeSeconds: elapsed, product: PRODUCT, moduleName, pageName})
    }
    prevRef.current = pathname
    enteredAtRef.current = Date.now()
  }, [pathname])

  return null
}

export default function AppRoutes() {
  const {userInfo} = useSelector((state) => state.user)
  const isAuthorized = userInfo.accessToken || false

  // Start heartbeat when user logs in, stop on logout
  useEffect(() => {
    if (isAuthorized) {
      startHeartbeat()
    } else {
      stopHeartbeat()
    }
  }, [isAuthorized])

  return (
    <BrowserRouter>
      <ScrollToTop />
      {isAuthorized && <NavigationTracker />}
      <Routes>
        {/* DEV-ONLY layout harness for the Weight Discrepancy / Weight Freeze
            modules — see components/weight/__harness__/WeightHarness.js.
            `process.env.NODE_ENV` is inlined by CRA at build time, so this
            branch is dead-code-eliminated from any production bundle and the
            route cannot exist there. Deliberately outside the isAuthorized
            check: both modules run on frozen dummy data with no network calls,
            and the point is to check layout without a login. */}
        {process.env.NODE_ENV === 'development' && (
          <Route path='/__weight-harness' element={<WeightHarness />} />
        )}
        <Route>
          {isAuthorized ? (
            <>
              <Route
                path='/*'
                element={
                  <>
                    <InactivityModal />
                    <PrivateRoutes />
                  </>
                }
              />
              <Route index element={<Navigate to='/home' />} />
            </>
          ) : (
            <>
              <Route element={<AuthLayout />}>
                <Route exact element={<LoginForm2 />} path='/login' />

                <Route exact element={<ForgotPassword />} path='/forgot-password' />
                <Route exact element={<ResetPassword />} path='/reset-password' />

                <Route exact element={<Otp />} path='/otp' />
                <Route exact element={<CreateNewPassword />} path='/create-new-password' />
                <Route exact element={<SelectLoginUser />} path='/loginuser' />
                <Route exact element={<MobileLogin />} path='/verify-otp' />
                <Route exact element={<Signup />} path='/signup' />
              </Route>

              <Route path='*' element={<Navigate to='/login' />} />

              <Route exact path='/toc' element={<TOC />} />
              <Route path='google/redirect_uri' element={<HandleGoogleRedirect />} />
              <Route path='/microsoft/redirect' element={<HandleMSALRedirect />} />
              <Route path='/privacy-policy' element={<PrivacyPolicy />} />
              <Route path='/refund_cancellation-policy' element={<RefundPolicy />} />
            </>
          )}
        </Route>
      </Routes>
    </BrowserRouter>
  )
}
